import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { makeStyles } from "$components/mui-styles";
import { RadioGroupField as RadioField } from "$components/rhf-fields";
import { Box, Typography } from "@material-ui/core";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

export enum ReportOption {
  WithoutHistory = "WITHOUT_HISTORY",
  WithHistory = "WITH_HISTORY",
}

export type DownloadReportModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  onSubmit?: (opt) => void;
};

const useStyles = makeStyles(() => ({
  rowCenter: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  warningIcon: {
    width: "50px",
    marginRight: "15px",
  },
  modalBody: {
    overflow: "auto",
  },
}));

const DownloadReportModal = ({
  isOpen,
  onClose,
  onSubmit,
}: DownloadReportModalProps) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [reportOption, setReportOption] = useState<string>(
    ReportOption.WithoutHistory,
  );

  const onFormSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      onSubmit(reportOption);
    } catch (err) {
      console.warn("On submit should be infallible, but it failed", err);
    } finally {
      // The error should be handled always by the callback implementer, onSubmit should never throw.
      setSubmitting(false);
    }
  };

  const classes = useStyles();
  const { formatMessage } = useIntl();
  const formMethods = useForm({
    mode: "onTouched",
  });

  return (
    <DialogContainer
      open={isOpen}
      onClose={onClose}
      data-testid="patient-history-download-report-modal"
    >
      <FormProvider {...formMethods}>
        <form onSubmit={onFormSubmit}>
          <DialogHeader title="">
            <Box
              className={classes.rowCenter}
              display="flex"
              flexDirection="row"
            >
              <img
                className={classes.warningIcon}
                src={warningIcon}
                alt="warning"
              />
              <Typography variant="h4">
                <FormattedMessage
                  id="patient.history.downloadReport.modal.title"
                  defaultMessage="Doctor's Report"
                />
              </Typography>
            </Box>
          </DialogHeader>
          <DialogBody className={classes.modalBody}>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="patient.history.downloadReport.modal.description"
                defaultMessage="Kindly select an option"
              />
            </Typography>
            <RadioField
              required
              data-testid="patient-history-download-report-options"
              name="reportOptions"
              value={reportOption}
              onChange={(e) => setReportOption(e.target.value)}
              options={[
                {
                  value: ReportOption.WithoutHistory,
                  label: formatMessage({
                    id: "patient.history.downloadReport.modal.option.withoutHistory",
                    defaultMessage: "Without History (For insurance claims)",
                  }),
                },
                {
                  value: ReportOption.WithHistory,
                  label: formatMessage({
                    id: "patient.history.downloadReport.modal.option.withHistory",
                    defaultMessage:
                      "With History (For patient transfers and referrals)",
                  }),
                },
              ]}
            />
          </DialogBody>
          <DialogFooter>
            <Button color="secondary" onClick={onClose}>
              <FormattedMessage
                id="patient.history.downloadReport.modal.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              data-testid="patient-history-download-report-submit-button"
            >
              <FormattedMessage
                id="patient.history.downloadReport.modal.submit"
                defaultMessage="Download"
              />
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};

export default DownloadReportModal;
